import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import '../../../styles/ExecutiveGold.css';

const ExecutiveGold = ({ isPreview = true }) => {
  const { eventName } = useParams();
  const [isRevealed, setIsRevealed] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [rsvpData, setRsvpData] = useState({
    name: '',
    email: '',
    company: '',
    position: '',
    attending: '',
    guests: '1',
    message: ''
  });

  // Parse event name
  const formatName = (name) => {
    if (!name) return 'Executive Summit';
    return name.split('-').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ');
  };

  const invitationData = {
    eventName: formatName(eventName),
    eventType: 'Exclusive Executive Summit',
    year: '2026',
    eventDate: 'December 5, 2026',
    eventDay: 'Saturday',
    eventTime: '6:00 PM - 10:00 PM',
    venue: 'The Peninsula Manila',
    venueAddress: 'Ayala Avenue, Makati City',
    organizer: 'Elite Business Group',
    message: 'You are exclusively invited to an evening of refined networking, insightful discussions, and exceptional dining with industry leaders.',
    highlights: [
      { icon: '🥂', title: 'Cocktail Reception', desc: 'Premium selections' },
      { icon: '🎤', title: 'Keynote Speaker', desc: 'Industry visionary' },
      { icon: '🍽️', title: 'Gala Dinner', desc: 'Five-star cuisine' },
      { icon: '🤝', title: 'Networking', desc: 'C-suite executives' }
    ],
    schedule: [
      { time: '6:00 PM', event: 'Arrival & Cocktails' },
      { time: '7:00 PM', event: 'Welcome Address' },
      { time: '7:30 PM', event: 'Keynote Presentation' },
      { time: '8:30 PM', event: 'Gala Dinner' },
      { time: '10:00 PM', event: 'Closing & Networking' }
    ]
  };

  useEffect(() => {
    const timer = setTimeout(() => setIsRevealed(true), 500);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="executive-gold">
      {/* Luxury Elements */}
      <div className="luxury-elements">
        <div className="gold-shimmer"></div>
        <div className="corner-ornament top-left">❖</div>
        <div className="corner-ornament top-right">❖</div>
        <div className="corner-ornament bottom-left">❖</div>
        <div className="corner-ornament bottom-right">❖</div>
        <div className="floating-particle p1"></div>
        <div className="floating-particle p2"></div>
        <div className="floating-particle p3"></div>
      </div>

      {/* Main Content */}
      <div className={`executive-container ${isRevealed ? 'revealed' : ''}`}>
        {/* Navigation */}
        <nav className="executive-nav">
          <button 
            className={activeSection === 'home' ? 'active' : ''} 
            onClick={() => setActiveSection('home')}
          >
            Invitation
          </button>
          <button 
            className={activeSection === 'details' ? 'active' : ''} 
            onClick={() => setActiveSection('details')}
          >
            Programme
          </button>
          <button 
            className={activeSection === 'rsvp' ? 'active' : ''} 
            onClick={() => setActiveSection('rsvp')}
          >
            RSVP
          </button>
        </nav>

        {/* Home Section */}
        {activeSection === 'home' && (
          <section className="executive-section home-section">
            <div className="executive-frame">
              <div className="luxury-border">
                <div className="border-accent top"></div>
                <div className="border-accent bottom"></div>
                
                <div className="home-content">
                  <div className="organizer-crest">
                    <span className="crest-icon">◆</span>
                    <span className="crest-text">{invitationData.organizer}</span>
                  </div>

                  <p className="invitation-prefix">Cordially Invites You to</p>

                  <div className="event-info">
                    <span className="event-year">{invitationData.year}</span>
                    <h1 className="event-title">{invitationData.eventName}</h1>
                    <span className="event-type">{invitationData.eventType}</span>
                  </div>

                  <div className="gold-divider">
                    <span className="divider-ornament">✦</span>
                  </div>

                  <p className="invitation-message">{invitationData.message}</p>

                  <div className="event-datetime">
                    <div className="datetime-item">
                      <span className="dt-label">Date</span>
                      <span className="dt-value">{invitationData.eventDate}</span>
                    </div>
                    <div className="datetime-divider">|</div>
                    <div className="datetime-item">
                      <span className="dt-label">Time</span>
                      <span className="dt-value">{invitationData.eventTime}</span>
                    </div>
                  </div>

                  <div className="venue-info">
                    <span className="venue-icon">📍</span>
                    <div className="venue-text">
                      <span className="venue-name">{invitationData.venue}</span>
                      <span className="venue-address">{invitationData.venueAddress}</span>
                    </div>
                  </div>

                  <button 
                    className="executive-btn"
                    onClick={() => setActiveSection('rsvp')}
                  >
                    RSVP Now
                  </button>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Details Section */}
        {activeSection === 'details' && (
          <section className="executive-section details-section">
            <h2 className="section-title">
              <span className="title-ornament">✦</span>
              Evening Programme
              <span className="title-ornament">✦</span>
            </h2>

            <div className="highlights-grid">
              {invitationData.highlights.map((item, index) => (
                <div key={index} className="highlight-card">
                  <span className="highlight-icon">{item.icon}</span>
                  <span className="highlight-title">{item.title}</span>
                  <span className="highlight-desc">{item.desc}</span>
                </div>
              ))}
            </div>

            <div className="schedule-section">
              <h3 className="schedule-title">Schedule of Events</h3>
              <div className="schedule-list">
                {invitationData.schedule.map((item, index) => (
                  <div key={index} className="schedule-item">
                    <span className="schedule-time">{item.time}</span>
                    <span className="schedule-dot">◆</span>
                    <span className="schedule-event">{item.event}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="dress-code-box">
              <span className="dress-icon">👔</span>
              <div className="dress-info">
                <span className="dress-label">Dress Code</span>
                <span className="dress-value">Black Tie / Formal Evening Attire</span>
              </div>
            </div>

            <button 
              className="executive-btn"
              onClick={() => setActiveSection('rsvp')}
            >
              Confirm Attendance
            </button>
          </section>
        )}

        {/* RSVP Section */}
        {activeSection === 'rsvp' && (
          <section className="executive-section rsvp-section">
            <h2 className="section-title">
              <span className="title-ornament">✦</span>
              RSVP
              <span className="title-ornament">✦</span>
            </h2>

            <form className="executive-rsvp-form" onSubmit={(e) => e.preventDefault()}>
              <p className="rsvp-intro">We request the pleasure of your reply</p>
              <p className="rsvp-deadline">Kindly respond by November 25, 2026</p>

              <div className="form-row">
                <div className="form-group">
                  <label>Full Name</label>
                  <input
                    type="text"
                    name="name"
                    value={rsvpData.name}
                    placeholder="Enter your full name"
                    disabled={isPreview}
                  />
                </div>
                <div className="form-group">
                  <label>Position / Title</label>
                  <input
                    type="text"
                    name="position"
                    value={rsvpData.position}
                    placeholder="e.g., CEO, Director"
                    disabled={isPreview}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Company</label>
                  <input
                    type="text"
                    name="company"
                    value={rsvpData.company}
                    placeholder="Company name"
                    disabled={isPreview}
                  />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    name="email"
                    value={rsvpData.email}
                    placeholder="Email address"
                    disabled={isPreview}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Will you be attending?</label>
                <div className="attendance-options">
                  <label className={`attendance-option ${isPreview ? 'disabled' : ''}`}>
                    <input
                      type="radio"
                      name="attending"
                      value="yes"
                      disabled={isPreview}
                    />
                    <span className="option-ornament">◆</span>
                    <span className="option-text">Accepts with Pleasure</span>
                  </label>
                  <label className={`attendance-option ${isPreview ? 'disabled' : ''}`}>
                    <input
                      type="radio"
                      name="attending"
                      value="no"
                      disabled={isPreview}
                    />
                    <span className="option-ornament">◇</span>
                    <span className="option-text">Regretfully Declines</span>
                  </label>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Number of Guests</label>
                  <select
                    name="guests"
                    value={rsvpData.guests}
                    disabled={isPreview}
                  >
                    <option value="1">1 Guest</option>
                    <option value="2">2 Guests</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Special Requests (Optional)</label>
                <textarea
                  name="message"
                  value={rsvpData.message}
                  placeholder="Dietary requirements or other requests..."
                  rows="2"
                  disabled={isPreview}
                />
              </div>

              <button type="submit" className="executive-btn full-width" disabled={isPreview}>
                Submit RSVP
              </button>
            </form>
          </section>
        )}

        {/* Watermark */}
        <div className="executive-watermark">
          <a href="/invitations" className="back-to-templates-btn">← Back to Templates</a>
          <p>Crafted by <a href="https://logicatechnology.com" target="_blank" rel="noopener noreferrer">Logica Technology</a></p>
        </div>
      </div>
    </div>
  );
};

export default ExecutiveGold;
